import { useState } from "react";
import { useOpenSessionByDefault } from "../../application/useOpenSessionByDefault";
import { useMentoringCourses } from "../../application/useMentoringCourses";
import { useTranslation } from "react-i18next";
import SchedulerLaunchSheet from "./SchedulerLaunchSheet";
import ScheduleSessionModal from "./ScheduleSessionModal";
import { useAlchelyMeetingAdopted } from "../../../marketplace";
import UpcomingSessionsModal from "./UpcomingSessionsModal";
import {
    useScheduleSession,
    useSchedulerSettings,
    useSpaces,
} from "../../application/useScheduler";
import { useSchedulerSubjects } from "../../application/useSchedulerSubjects";
import { toDateKey } from "../../domain/formatting";
import type { SubjectKind } from "../../domain/types";

interface Props {
    isOpen: boolean;
    onClose: () => void;
    /**
     * Fixed when the dock sits on a patient or couple page; absent on the home
     * page, where the pickers come back because no subject is in context.
     */
    subject?: { kind: SubjectKind; id: string; label: string } | null;
}

/**
 * Everything the Agendar dock entry opens: the launch sheet, the scheduling
 * modal and the upcoming-sessions view.
 *
 * Lives here rather than in either dock so the two behave identically, and so
 * the docks themselves stay free of scheduler data — this mounts only once the
 * tool is adopted, which is also what keeps its queries from running for
 * therapists who do not have it.
 */
export default function SchedulerDockActions({
    isOpen,
    onClose,
    subject = null,
}: Props) {
    const { t } = useTranslation();
    const openSessionByDefault = useOpenSessionByDefault();
    const mentoringCourses = useMentoringCourses();
    const [scheduleOpen, setScheduleOpen] = useState(false);
    const [upcomingOpen, setUpcomingOpen] = useState(false);
    const [bookingError, setBookingError] = useState<string | null>(null);

    // Only fetched when the caller has no subject — a patient page already
    // knows who it is booking for and needs no picker.
    const subjects = useSchedulerSubjects(!subject);

    const settingsQuery = useSchedulerSettings();
    const spacesQuery = useSpaces();
    const scheduleSession = useScheduleSession();
    const alchelyMeetingAvailable = useAlchelyMeetingAdopted();
    const timezone = settingsQuery.data?.timezone ?? "UTC";

    return (
        <>
            <SchedulerLaunchSheet
                isOpen={isOpen}
                onClose={onClose}
                onSchedule={() => setScheduleOpen(true)}
                onShowUpcoming={() => setUpcomingOpen(true)}
                subjectLabel={subject?.label ?? null}
            />

            {settingsQuery.data && (
                <ScheduleSessionModal
                    alchelyMeetingAvailable={alchelyMeetingAvailable}
                    openSessionByDefault={openSessionByDefault}
                    courses={mentoringCourses}
                    open={scheduleOpen}
                    onClose={() => setScheduleOpen(false)}
                    settings={settingsQuery.data}
                    spaces={spacesQuery.data ?? []}
                    subject={subject}
                    patients={subjects.options}
                    initialDate={toDateKey(new Date(), timezone)}
                    isSaving={scheduleSession.isPending}
                    errorMessage={bookingError}
                    onSubmit={(payload) => {
                        setBookingError(null);
                        scheduleSession.mutate(payload, {
                            onSuccess: () => setScheduleOpen(false),
                            onError: (error: unknown) => {
                                const status = (
                                    error as { response?: { status?: number } }
                                )?.response?.status;
                                setBookingError(
                                    status === 409
                                        ? t("scheduler.schedule.slotTaken")
                                        : t("scheduler.schedule.scheduleError")
                                );
                            },
                        });
                    }}
                />
            )}

            <UpcomingSessionsModal
                isOpen={upcomingOpen}
                onClose={() => setUpcomingOpen(false)}
                timezone={timezone}
                subject={subject ? { kind: subject.kind, id: subject.id } : null}
                subjectLabel={subject?.label ?? null}
                options={subjects.options}
                optionsFailed={subjects.isError}
                names={subjects.names}
                onSchedule={() => {
                    setUpcomingOpen(false);
                    setScheduleOpen(true);
                }}
            />
        </>
    );
}
